import { EventBroker } from 'BMapsSrc/eventbroker';
import { UserActions } from 'BMapsCmds';
import { App } from '../BMapsApp';
import { ZapReason } from '../model/zapReason';
import DataConnection from '../DataConnection';
import { ServerConnection } from './ServerConnection';
import { StaticDataConnection } from './StaticDataConnection';
import { isDisconnectedSession, SessionErrorType } from './session_utils';
import { SESSION_RESTORE_EVENT, SessionRestoreStatus } from './sessionRestore';
import { LoadingScreen } from '../LoadingScreen';

export async function getSelectivityDataConnection() {
    const unused = App.ConnectionManager.getUnusedDataConnection(ServerConnection);
    if (unused) return { dataConnection: unused };

    if (App.PrimaryDataConnection.getMode() === 'static') {
        const { dataConnection } = await App.ConnectionManager.newStaticConnection();
        return { dataConnection };
    }

    const { dataConnection, errorInfo } = await LoadingScreen.withLoadingScreen(
        App.ConnectionManager.newServerConnection(), 'Establishing a Selectivity Session...'
    );

    if (dataConnection) {
        return { dataConnection };
    }

    // Couldn't get server connection so maybe get static connection
    const { errType } = errorInfo;
    let alert = '';
    switch (errType) {
        case SessionErrorType.CantStart_SessionLimitReached:
            alert = 'You have no compute resources available for selectivity';
            break;
        case SessionErrorType.CantStart_ServerUnavailable:
            alert = 'There are no compute resources available for selectivity';
            break;
        case SessionErrorType.CantStart_NotLoggedIn:
            // User must be in static / Preview mode. No need to alert.
            alert = null;
            break;
        default:
            alert = 'There was a problem establishing a selectivity session';
    }
    if (alert) {
        alert += ', but an additional structure can be loaded for visualization only.';
    }

    return { failureReason: alert };
}

export async function getNewStaticDataConnection() {
    return (await App.ConnectionManager.newStaticConnection()).dataConnection;
}

export async function releaseUnusedConnections() {
    let unused = App.ConnectionManager.getUnusedDataConnection(ServerConnection);
    if (unused && unused !== App.PrimaryDataConnection) {
        await App.ConnectionManager.removeDataConnection(unused);
        // Overloading serverQueueUpdate to refresh ServerInfo
        EventBroker.publish('serverQueueUpdate');
    }
    unused = App.ConnectionManager.getUnusedDataConnection(StaticDataConnection);
    if (unused && unused !== App.PrimaryDataConnection) {
        await App.ConnectionManager.removeDataConnection(unused);
        // Overloading serverQueueUpdate to refresh ServerInfo
        EventBroker.publish('serverQueueUpdate');
    }
}

/**
 * Save state, zap all connections, create new connection, and restore state.
 *
 * Both zaps below — the explicit one and the one inside `RestoreState` — are
 * tagged `Reconnect`. This is the path the user takes by clicking the button
 * whose whole purpose is not to lose their work, so a panel must be able to tell
 * it apart from a discard. A bare `zapAll` reads as "the user threw this away",
 * and the campaign panel acted on it: cancelled its executor and cleared the
 * plan, the step states and a produced narrative that `SavedState` never carried
 * in the first place.
 *
 * The zap has to be tagged before the outcome is known, because the zap comes
 * first. So every exit below reports whether the workspace actually came back,
 * on `SESSION_RESTORE_EVENT` — a panel that spared its state has state describing
 * a workspace that is not there until this says otherwise, and it must not go on
 * dispatching against it.
 *
 * @param {bool} kickOutOldSession - If true, close an existing session to make room for the new one
 */
export async function reconnectAndRestoreSession(kickOutOldSession) {
    const msg = 'Reestablishing Session...';
    const zapOptions = { reason: ZapReason.Reconnect };
    const reportRestore = (status, reason) => {
        EventBroker.publish(SESSION_RESTORE_EVENT, { status, reason });
    };

    let state;
    try {
        state = UserActions.CaptureState();
    } catch (ex) {
        console.error(`Failed to capture state when retrying session: ${ex}`);
    }

    // Remove all connections
    await LoadingScreen.withLoadingScreen(App.ConnectionManager.removeAllConnections(), msg);
    App.Workspace.zap(zapOptions);

    // Start a new connection
    const { errorInfo } = await LoadingScreen.withLoadingScreen(
        App.ConnectionManager.newServerConnection(kickOutOldSession, false), msg
    );
    if (errorInfo) {
        EventBroker.publish('sessionError', errorInfo);
        reportRestore(
            SessionRestoreStatus.Unavailable,
            'The session could not be reestablished, so this workspace was not restored.',
        );
    } else if (!state) {
        // Reconnected, but there was nothing to put back: the capture above threw.
        reportRestore(
            SessionRestoreStatus.Unavailable,
            'This workspace could not be captured before reconnecting, so it was not restored.',
        );
    } else {
        const { errors } = await UserActions.RestoreState(state, true, zapOptions);
        if (errors && errors.length > 0) {
            reportRestore(
                SessionRestoreStatus.Unavailable,
                `This workspace was only partly restored (${errors.length} problem`
                + `${errors.length === 1 ? '' : 's'}), so it may not match what the run measured.`,
            );
        } else {
            reportRestore(SessionRestoreStatus.Restored);
        }
    }
}
